import { z } from 'zod'

/**
 * Heimdall BC Sales Order contracts, reached through Vision's byte-passthrough
 * proxy (`/v1/bc/sales-orders/from-po`, `.../from-po/lines`, and
 * `.../from-po/lots`). The bodies below are Heimdall's
 * `CreateBcSalesOrderFromPoResponse` / `AddBcSalesOrderLinesResponse` /
 * `AssignBcLotsResponse` verbatim — Vision does not reshape them.
 *
 * Heimdall runs Jackson's default inclusion, so absent values arrive as
 * explicit `null` rather than being omitted. Every optional field is therefore
 * `.nullish()`, not `.optional()` — a plain `.optional()` rejects `null` and
 * would turn a perfectly good response into a "malformed response" toast.
 *
 * Fields Heimdall sends for debugging only (`requestUrl`, `requestBody`,
 * `curl`, `bcSystemId`, `soCount`) are deliberately not modelled; Zod strips
 * them.
 */

/**
 * Per-SO header outcome. Only `status` is guaranteed — the rest are
 * best-effort and are null on a row that failed before BC was contacted.
 *
 * ```json
 * {
 *   "externalPoNumber": "5KZZMABE",
 *   "results": [
 *     { "externalDocumentNo": "5KZZMABE-1", "vinculumSo": "M7718",
 *       "status": "CREATED", "bcSalesOrderNo": "SO-000123", "message": null }
 *   ]
 * }
 * ```
 */
export const BcSalesOrderResultStatusSchema = z.enum(['CREATED', 'DRY_RUN', 'SKIPPED', 'FAILED'])
export type BcSalesOrderResultStatus = z.infer<typeof BcSalesOrderResultStatusSchema>

export const BcSalesOrderFromPoResultSchema = z.object({
  externalDocumentNo: z.string().nullish(),
  vinculumSo: z.string().nullish(),
  status: BcSalesOrderResultStatusSchema,
  bcSalesOrderNo: z.string().nullish(),
  locationCode: z.string().nullish(),
  message: z.string().nullish(),
})
export type BcSalesOrderFromPoResult = z.infer<typeof BcSalesOrderFromPoResultSchema>

export const BcSalesOrderFromPoResponseSchema = z.object({
  externalPoNumber: z.string(),
  results: z.array(BcSalesOrderFromPoResultSchema),
})
export type BcSalesOrderFromPoResponse = z.infer<typeof BcSalesOrderFromPoResponseSchema>

/**
 * Per-line outcome inside an {@link AddBcSalesOrderLinesResult}. `NOT_ATTEMPTED`
 * marks the lines Heimdall abandoned after an earlier line on the same SO
 * failed, so a partially-added SO is still legible.
 */
export const BcSalesOrderLineActionSchema = z.enum([
  'DRY_RUN',
  'ADDED',
  'SKIPPED_EXISTS',
  'NOT_ATTEMPTED',
])
export type BcSalesOrderLineAction = z.infer<typeof BcSalesOrderLineActionSchema>

export const BcSalesOrderLineSchema = z.object({
  itemNo: z.string().nullish(),
  quantity: z.number().nullish(),
  unitOfMeasureCode: z.string().nullish(),
  action: BcSalesOrderLineActionSchema,
})
export type BcSalesOrderLine = z.infer<typeof BcSalesOrderLineSchema>

/**
 * Per-SO line-add outcome. `NO_PACKED_LINES` is Heimdall telling us the SO has
 * no packed lines to push yet — an operator-actionable state (pack it in
 * Vinculum first), not a success, so the UI surfaces it as an error.
 */
export const AddBcSalesOrderLinesStatusSchema = z.enum([
  'DRY_RUN',
  'ADDED',
  'SKIPPED',
  'FAILED',
  'NO_PACKED_LINES',
])
export type AddBcSalesOrderLinesStatus = z.infer<typeof AddBcSalesOrderLinesStatusSchema>

export const AddBcSalesOrderLinesResultSchema = z.object({
  externalDocumentNo: z.string().nullish(),
  vinculumSo: z.string().nullish(),
  bcSalesOrderNo: z.string().nullish(),
  locationCode: z.string().nullish(),
  status: AddBcSalesOrderLinesStatusSchema,
  message: z.string().nullish(),
  lines: z.array(BcSalesOrderLineSchema).nullish(),
})
export type AddBcSalesOrderLinesResult = z.infer<typeof AddBcSalesOrderLinesResultSchema>

export const AddBcSalesOrderLinesResponseSchema = z.object({
  externalPoNumber: z.string(),
  results: z.array(AddBcSalesOrderLinesResultSchema),
})
export type AddBcSalesOrderLinesResponse = z.infer<typeof AddBcSalesOrderLinesResponseSchema>

/**
 * Whether lots can be assigned on a Sales Order yet
 * (`GET /v1/bc/sales-orders/from-po/lots/readiness`). One call answers for
 * every SO under a PO.
 *
 * `readiness` is a **bare string, not an enum**, for the same reason
 * `bcStatus` is one in `@/lib/vision/schemas`: this GET runs on every PO detail
 * render, so a token Heimdall adds later must parse rather than take the whole
 * page down on a Pulse build that predates it. `bcLotState` maps the tokens and
 * keeps anything it does not recognise renderable-but-inert.
 *
 * The tokens Heimdall sends today:
 *
 *   - `NO_BC_SALES_ORDER` — no BC Sales Order header exists yet.
 *   - `NO_LINES`          — the header is there, BC holds no lines.
 *   - `READY`             — BC holds lines and no assignment has settled. Lots
 *                           can go on.
 *   - `ASSIGNED`          — a real assignment has already settled and Heimdall
 *                           persisted it; `lotsAssignedAt` / `lotLineCount` set.
 *   - `UNKNOWN`           — Heimdall could not reach Business Central to look.
 *
 * That last one is not a flavour of `READY` and must never render as one: "we
 * could not see" and "we looked and the lines are there" are different facts,
 * and this repo keeps them apart everywhere else too.
 *
 * `AWAITING_BOX_QTY` was a sixth token, sent while the BC sales lines did not
 * all carry a box quantity an operator had typed in by hand. Heimdall derives
 * that count itself now and writes it at line-create, so the check was reading
 * back its own write and was retired in `heimdall@b9bd28f`; the rows it used to
 * describe arrive as `READY`. Nothing replaced it, and nothing should — see
 * `linesWithBoxQty` below before rebuilding a rule on the count.
 */
export const BcLotReadinessResultSchema = z.object({
  externalDocumentNo: z.string().nullish(),
  vinculumSo: z.string().nullish(),
  bcSalesOrderNo: z.string().nullish(),
  readiness: z.string().nullish(),
  message: z.string().nullish(),
  lineCount: z.number().nullish(),
  // Still sent, and still modelled, but nothing reads it: Heimdall redocumented
  // it as a diagnostic when the gate went, and a zero now means nobody derived a
  // count for that line rather than that somebody still has to enter one. Kept
  // parseable so it can be shown as a number if that is ever wanted; do not make
  // it a readiness rule again.
  linesWithBoxQty: z.number().nullish(),
  lotsAssignedAt: z.string().nullish(),
  lotLineCount: z.number().nullish(),
})
export type BcLotReadinessResult = z.infer<typeof BcLotReadinessResultSchema>

export const BcLotReadinessResponseSchema = z.object({
  externalPoNumber: z.string(),
  results: z.array(BcLotReadinessResultSchema),
})
export type BcLotReadinessResponse = z.infer<typeof BcLotReadinessResponseSchema>

/**
 * One reservation entry Business Central picked, echoed back so the operator
 * can see which lot went against which line without opening BC.
 */
export const BcLotAllocationSchema = z.object({
  lineNo: z.number().nullish(),
  itemNo: z.string().nullish(),
  locationCode: z.string().nullish(),
  lotNo: z.string().nullish(),
  quantityBase: z.number().nullish(),
  expirationDate: z.string().nullish(),
})
export type BcLotAllocation = z.infer<typeof BcLotAllocationSchema>

/**
 * Per-SO lot-assignment outcome (`POST /v1/bc/sales-orders/from-po/lots`).
 *
 *   - `ASSIGNED`          — reservation entries were written. Success.
 *   - `NOTHING_TO_DO`     — every line already carried tracking. An idempotent
 *                           replay finding the work already done is a success,
 *                           not a fault, and `summarizeBcLotResults` toasts it
 *                           as one.
 *   - `NO_BC_SALES_ORDER` — the header/lines steps have not run. The fix is to
 *                           run them, not to go looking in BC, so it is kept
 *                           distinct from `FAILED`.
 *   - `FAILED`            — `message` carries Heimdall's operator-facing reason
 *                           and already distinguishes a lot shortfall (BC 400,
 *                           naming the offending lines) from a missing BC grant
 *                           (403) and an unpublished AL extension (404). It is
 *                           surfaced verbatim.
 *   - `PLANNED`           — dry-run only. Pulse never sends `dryRun: true`, so
 *                           it is unexpected, but it stays renderable.
 *
 * An enum here rather than a bare string, matching the two sibling write
 * responses above: unlike the readiness GET this body is only ever seen after a
 * deliberate press, so a token we cannot parse costs one toast rather than the
 * PO page.
 */
export const BcLotAssignmentStatusSchema = z.enum([
  'PLANNED',
  'ASSIGNED',
  'NOTHING_TO_DO',
  'FAILED',
  'NO_BC_SALES_ORDER',
])
export type BcLotAssignmentStatus = z.infer<typeof BcLotAssignmentStatusSchema>

export const AssignBcLotsResultSchema = z.object({
  externalDocumentNo: z.string().nullish(),
  vinculumSo: z.string().nullish(),
  bcSalesOrderNo: z.string().nullish(),
  status: BcLotAssignmentStatusSchema,
  message: z.string().nullish(),
  allocations: z.array(BcLotAllocationSchema).nullish(),
})
export type AssignBcLotsResult = z.infer<typeof AssignBcLotsResultSchema>

export const AssignBcLotsResponseSchema = z.object({
  externalPoNumber: z.string(),
  results: z.array(AssignBcLotsResultSchema),
})
export type AssignBcLotsResponse = z.infer<typeof AssignBcLotsResponseSchema>
